import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Plus, Search, X, Shield, UserCheck, User as UserIcon, RefreshCw, Mail, Key,
} from 'lucide-react';
import CampoSenha from '../../components/CampoSenha';
import RequisitosDeSenha from '../../components/RequisitosDeSenha';
import {
  AcoesDaLinha, BotaoEditar, BotaoExcluir, BotaoAtivar, BotaoDesativar,
} from '../../components/BotoesAcao';
import { usuariosService, UsuarioData, RoleInfo } from '../../services/usuarios';
import { useConfirm } from '../../components/ConfirmDialog';
import { authService } from '../../services/auth';
import { formatDate } from '../../utils/formatters';
import toast from 'react-hot-toast';
import { mensagemDoErro } from '../../utils/erros';

const roleIcons: Record<string, React.ReactNode> = {
  admin: <Shield className="h-4 w-4 text-red-500" />,
  gestor: <UserCheck className="h-4 w-4 text-blue-500" />,
  operador: <UserIcon className="h-4 w-4 text-gray-500" />,
};

const roleColors: Record<string, string> = {
  admin: 'bg-red-100 text-red-700',
  gestor: 'bg-blue-100 text-blue-700',
  operador: 'bg-gray-100 text-gray-700',
};

const roleLabels: Record<string, string> = {
  admin: 'Administrador',
  gestor: 'Gestor',
  operador: 'Operador',
};

const UsuariosList: React.FC = () => {
  const confirm = useConfirm();
  const [usuarios, setUsuarios] = useState<UsuarioData[]>([]);
  const [roles, setRoles] = useState<RoleInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroRole, setFiltroRole] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UsuarioData | null>(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', password_confirmation: '', role: 'operador' });

  const currentUser = authService.getUser();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes] = await Promise.all([
        usuariosService.getAll({ search, role: filtroRole, page }),
        usuariosService.getRoles(),
      ]);
      if (usersRes.success) {
        setUsuarios(usersRes.data);
        setTotalPages(usersRes.pagination?.last_page || 1);
      }
      if (rolesRes.success) setRoles(rolesRes.data);
    } catch (err: any) {
      toast.error(mensagemDoErro(err, 'Erro ao carregar os usuários'));
    } finally {
      setLoading(false);
    }
  }, [search, filtroRole, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Volta para a primeira página ao mudar busca/filtro
  useEffect(() => { setPage(1); }, [search, filtroRole]);

  const openCreate = () => {
    setEditingUser(null);
    setForm({ name: '', email: '', password: '', password_confirmation: '', role: 'operador' });
    setShowModal(true);
  };

  const openEdit = (user: UsuarioData) => {
    setEditingUser(user);
    setForm({ name: user.name, email: user.email, password: '', password_confirmation: '', role: user.role });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Confere aqui para o erro aparecer antes da ida ao servidor: uma senha
    // digitada errada duas vezes seria aceita, e so apareceria no primeiro login.
    if (form.password && form.password !== form.password_confirmation) {
      toast.error('A confirmação não confere com a senha digitada.');
      return;
    }

    try {
      if (editingUser) {
        const data: any = { name: form.name, email: form.email, role: form.role };
        if (form.password) {
          data.password = form.password;
          data.password_confirmation = form.password_confirmation;
        }
        const res = await usuariosService.update(editingUser.id, data);
        if (res.success) { toast.success('Usuário atualizado'); }
      } else {
        if (!form.password) { toast.error('A senha é obrigatória'); return; }
        const res = await usuariosService.create(form);
        if (res.success) { toast.success('Usuário criado'); }
      }
      setShowModal(false);
      fetchData();
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      if (errors) { toast.error(Object.values(errors).flat()[0] as string); }
      else { toast.error(err?.response?.data?.message || 'Erro ao salvar'); }
    }
  };

  const handleToggleStatus = async (user: UsuarioData) => {
    const desativando = user.ativo;
    if (!(await confirm({
      title: desativando ? 'Desativar usuário' : 'Ativar usuário',
      message: desativando
        ? `"${user.name}" perde o acesso imediatamente e as sessões abertas são encerradas. O histórico dele continua no sistema.`
        : `"${user.name}" volta a ter acesso ao sistema com o mesmo perfil de antes.`,
      confirmLabel: desativando ? 'Desativar' : 'Ativar',
      variant: desativando ? 'danger' : 'primary',
    }))) return;
    try {
      const res = await usuariosService.toggleStatus(user.id);
      if (res.success) { toast.success(res.message); fetchData(); }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erro ao alterar o status');
    }
  };

  const handleDelete = async (user: UsuarioData) => {
    if (!(await confirm({
      title: 'Remover usuário',
      message: `Tem certeza que deseja remover "${user.name}"? Esta ação não pode ser desfeita.`,
      confirmLabel: 'Remover',
      variant: 'danger',
    }))) return;
    try {
      const res = await usuariosService.remove(user.id);
      if (res.success) { toast.success('Usuário removido'); fetchData(); }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erro ao remover');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usuários</h1>
          <p className="text-gray-500 mt-1">Gerenciar usuários e permissões da empresa</p>
        </div>
        <button onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          <Plus className="h-4 w-4" /> Novo usuário
        </button>
      </div>

      {/* Permissoes */}
      <div className="bg-white rounded-xl border p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Perfis de permissão</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {roles.map((role) => (
            <div key={role.value} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="mt-0.5">{roleIcons[role.value]}</div>
              <div>
                <p className="text-sm font-medium text-gray-900">{role.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{role.descricao}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nome ou e-mail..."
            className="pl-10 w-full py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
        </div>
        <select value={filtroRole} onChange={e => setFiltroRole(e.target.value)}
          className="px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500">
          <option value="">Todos os perfis</option>
          <option value="admin">Administrador</option>
          <option value="gestor">Gestor</option>
          <option value="operador">Operador</option>
        </select>
        <button onClick={fetchData}
          className="p-2.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg border">
          <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Lista */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Usuário</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">E-mail</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Perfil</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Criado em</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {usuarios.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center">
                        <UserIcon className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        {currentUser?.id === user.id && (
                          <span className="text-xs text-blue-600">(você)</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-600">{user.email}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${roleColors[user.role]}`}>
                      {roleIcons[user.role]} {roleLabels[user.role] || user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                      user.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {user.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-500">{formatDate(user.created_at)}</p>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <AcoesDaLinha>
                      <BotaoEditar onClick={() => openEdit(user)} />
                      {currentUser?.id !== user.id && (
                        user.ativo
                          ? <BotaoDesativar onClick={() => handleToggleStatus(user)} title="Desativar acesso" />
                          : <BotaoAtivar onClick={() => handleToggleStatus(user)} title="Ativar acesso" />
                      )}
                      {currentUser?.id !== user.id && (
                        <BotaoExcluir onClick={() => handleDelete(user)} title="Remover usuário" />
                      )}
                    </AcoesDaLinha>
                  </td>
                </tr>
              ))}
              {usuarios.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-400">
                    <Users className="h-10 w-10 mx-auto mb-3" />
                    <p>Nenhum usuário encontrado</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <p className="text-sm text-gray-600">Página {page} de {totalPages}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próximo
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal Criar/Editar */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold">{editingUser ? 'Editar usuário' : 'Novo usuário'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                    className="pl-10 w-full py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nome completo" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                    className="pl-10 w-full py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="email@empresa.com" />
                </div>
              </div>
              <div>
                <label htmlFor="senha-usuario" className="block text-sm font-medium text-gray-700 mb-1">
                  Senha {editingUser ? '(deixe vazio para manter)' : '*'}
                </label>
                <CampoSenha
                  id="senha-usuario"
                  icone={Key}
                  value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  placeholder={editingUser ? 'Nova senha (opcional)' : 'Crie uma senha forte'}
                  minLength={form.password ? 8 : 0}
                  required={!editingUser}
                  autoComplete="new-password"
                />
                {(form.password || !editingUser) && (
                  <>
                    <p className="mt-2 text-xs font-medium text-gray-700">A senha precisa ter, obrigatoriamente:</p>
                    <RequisitosDeSenha senha={form.password} />
                  </>
                )}
              </div>
              {(form.password || !editingUser) && (
                <div>
                  <label htmlFor="confirmar-senha-usuario" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirmar senha {editingUser ? '' : '*'}
                  </label>
                  <CampoSenha
                    id="confirmar-senha-usuario"
                    icone={Key}
                    value={form.password_confirmation}
                    onChange={e => setForm({...form, password_confirmation: e.target.value})}
                    placeholder="Repita a senha"
                    minLength={form.password ? 8 : 0}
                    required={Boolean(form.password) || !editingUser}
                    autoComplete="new-password"
                  />
                  {form.password_confirmation && form.password !== form.password_confirmation && (
                    <p role="alert" className="text-xs text-red-600 mt-1">
                      A confirmação não confere com a senha digitada.
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Perfil *</label>
                <select required value={form.role} onChange={e => setForm({...form, role: e.target.value})}
                  className="w-full py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <option value="admin">Administrador: acesso total, incluindo gerenciar usuários</option>
                  <option value="gestor">Gestor - Visualizar e editar registros</option>
                  <option value="operador">Operador - Cadastros operacionais</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancelar</button>
                <button type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  {editingUser ? 'Salvar' : 'Criar usuário'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsuariosList;
